import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Formik, Form } from 'formik';

import {
  UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_ACCOUNT_TRANSFER,
  UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_BOOKED_DIRECTLY,
} from 'common/utility/constants';

import {
  SR_API_ACTION_UTILITY_ACTIVATION_TRACK_INTERNET_FLOW,
  SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM,
} from 'api/utilityActionTypes';

import { ANALYTICS_EVENT_UTILITY_SETUP_INTERNET_FLOW_TRANSFER_REASON } from 'client/analytics/eventList';

import { hasRoleAnonymous } from 'common/utility/userRoles';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import UtilityActivationActions from 'client/actions/UtilityActivation';

import { createFormValidationFn, createInitialValues } from 'client/helpers/formikHelpers';
import useBranch from 'client/hooks/useBranch';

import {
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_YELLOW,
} from 'client/components/SrButton/SrButton';

import SrBox from 'client/components/SrBox';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrUtilitySetupLazySignup from 'client/components/SrUtilitySetup/SrUtilitySetupLazySignup';

const dropoutReasonExplanationFormFields = [
  {
    id: 'dropoutReasonExplanation',
    title: 'Note for landlord',
    subtitle: "We're always looking to improve!",
    type: 'long_text',
    properties: {
      placeholder: 'type here...',
      style: {
        height: '130px',
      },
    },
    validations: ['required'],
  },
];

const SrUtilitySetupTransferedAccountModal = ({
  utilityActivation,
  utilityActivationItem,
  onClose,
}) => {
  const [isExplanationModalOpen, setIsExplanationModalOpen] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [callbackValues, setCallbackValues] = useState(null);
  const { utilityUser } = useBranch({
    utilityUser: ['utilityUser'],
  });

  const dispatch = useDispatch();
  const history = useHistory();

  const trackInternetFlowEvent = (eventName) => {
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_TRACK_INTERNET_FLOW,
      payload: {
        utilityActivationId: utilityActivation.id,
        internetFlowEventName: eventName,
      },
    });
  };

  const onOpenExplanationModal = () => {
    return trackInternetFlowEvent(ANALYTICS_EVENT_UTILITY_SETUP_INTERNET_FLOW_TRANSFER_REASON)
      .then(() => {
        setIsExplanationModalOpen(true);
      })
      .catch(() => {
        setIsExplanationModalOpen(true);
      });
  };

  const onConfirm = ({ dropoutReason, dropoutReasonExplanation }) => {
    if (
      dropoutReason === UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_BOOKED_DIRECTLY &&
      !dropoutReasonExplanation
    ) {
      return onOpenExplanationModal();
    }

    if (hasRoleAnonymous(utilityUser.roles) && !showSignup) {
      setCallbackValues({
        dropoutReason,
        dropoutReasonExplanation,
      });
      setShowSignup(true);
      return;
    }

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM,
      payload: {
        id: utilityActivationItem.id,
        updates: {
          dropoutReason,
          dropoutReasonExplanation: dropoutReasonExplanation || null,
        },
      },
      onSuccess: () => {
        dispatch(UtilityActivationActions.getByShortId, {
          utilityActivationShortId: utilityActivation.shortId,
          forceRefresh: true,
        }).then(() => {
          history.push(
            `/utility-setup/${utilityActivation.partner.slug}/${utilityActivation.shortId}`,
          );
        });
      },
    });
  };

  const onSubmitExplanationModal = ({ dropoutReasonExplanation }) => {
    return onConfirm({
      dropoutReason: UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_BOOKED_DIRECTLY,
      dropoutReasonExplanation,
    });
  };

  const onCloseExplanationModal = () => {
    setIsExplanationModalOpen(null);
  };

  return (
    <>
      <SrSimpleMessageModal
        iconUrl={utilityActivationItem.type.iconUrl}
        headline={`Confirm when you purchased ${utilityActivationItem.type.label.toLowerCase()}.`}
        underline
        maxWidth={400}
        theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
        srMultiButtons={[
          {
            buttonText: 'Purchased recently',
            onButtonClick: () =>
              onConfirm({
                dropoutReason: UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_BOOKED_DIRECTLY,
              }),
            buttonTheme: SR_BUTTON_THEME_ADOBE_GREEN,
          },
          {
            buttonText: 'Transferring from my prior home',
            onButtonClick: () =>
              onConfirm({
                dropoutReason: UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_ACCOUNT_TRANSFER,
              }),
            buttonTheme: SR_BUTTON_THEME_ADOBE_YELLOW,
            buttonProps: {
              wrapText: true,
            },
          },
          {
            buttonText: 'Cancel',
            onButtonClick: onClose,
            buttonTheme: SR_BUTTON_THEME_ADOBE_GREY,
          },
        ]}
      />
      {isExplanationModalOpen && (
        <Formik
          initialValues={createInitialValues({
            formFields: dropoutReasonExplanationFormFields,
          })}
          validate={createFormValidationFn(dropoutReasonExplanationFormFields)}
          onSubmit={onSubmitExplanationModal}
          render={(formikBag) => (
            <Form>
              <SrSimpleMessageModal
                iconUrl="/images/green-question-mark.png"
                headline="Why did you purchase outside of this tool?"
                underline
                maxWidth={400}
                message={dropoutReasonExplanationFormFields.map((formField) => (
                  <SrBox textAlign="left" key={formField.id}>
                    <SrFormikField
                      formField={formField}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                  </SrBox>
                ))}
                theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
                srMultiButtons={[
                  {
                    buttonText: 'Submit',
                    onButtonClick: formikBag.handleSubmit,
                    buttonTheme: SR_BUTTON_THEME_ADOBE_GREEN,
                  },
                  {
                    buttonText: 'Back',
                    onButtonClick: onCloseExplanationModal,
                    buttonTheme: SR_BUTTON_THEME_ADOBE_GREY,
                  },
                ]}
              />
            </Form>
          )}
        />
      )}
      {showSignup && (
        <SrUtilitySetupLazySignup
          utilityActivation={utilityActivation}
          callbackFunction={() => {
            onConfirm(callbackValues);
          }}
          onClose={() => setShowSignup(false)}
        />
      )}
    </>
  );
};

SrUtilitySetupTransferedAccountModal.propTypes = {
  utilityActivation: PropTypes.object.isRequired,
  utilityActivationItem: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

SrUtilitySetupTransferedAccountModal.displayName = 'SrUtilitySetupTransferedAccountModal';

export default SrUtilitySetupTransferedAccountModal;
